import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Calendar, Receipt, AlertCircle, ArrowRight, Sparkles, XCircle, Settings, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { formatBRL, PLANS } from '../lib/plans';
import { createCustomerPortal, createCheckoutSession } from '../lib/billingClient';
import PlanBadge from '../components/PlanBadge';

const statusLabel = {
  active: { label: 'Ativa', color: 'green' },
  trialing: { label: 'Período de teste', color: 'blue' },
  past_due: { label: 'Em atraso', color: 'orange' },
  canceled: { label: 'Cancelada', color: 'red' },
  expired: { label: 'Expirada', color: 'gray' },
  incomplete: { label: 'Incompleta', color: 'orange' },
};

const Billing = () => {
  const { user, profile } = useAuth();
  const { plan, subscription, refresh } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  const intent = new URLSearchParams(location.search).get('intent');
  const intentPlanId = new URLSearchParams(location.search).get('plan');

  useEffect(() => {
    if (!user) return;
    (async () => {
      const [{ data: pays }, { data: invs }] = await Promise.all([
        supabase.from('payments').select('*').eq('user_id', user.id).order('created_at', { ascending: false }).limit(10),
        supabase.from('invoices').select('*').eq('user_id', user.id).order('created_at', { ascending: false }).limit(10),
      ]);
      setPayments(pays || []);
      setInvoices(invs || []);
      setLoading(false);
    })();
  }, [user]);

  const openCustomerPortal = async () => {
    if (!user) return;
    setPortalLoading(true);
    setActionMsg('');
    try {
      const res = await createCustomerPortal({
        userId: user.id,
        email: user.email || profile?.email,
      });
      if (res?.url) {
        window.location.href = res.url;
      } else {
        throw new Error('URL do portal não retornada.');
      }
    } catch (e) {
      setActionMsg(`Erro ao abrir o portal: ${e.message || e}`);
      setPortalLoading(false);
    }
  };

  const handleCancel = async () => {
    // Use Stripe Customer Portal for cancellations (the source of truth).
    // It will fire webhook → updates Supabase subscriptions automatically.
    await openCustomerPortal();
  };

  const handleUpgradeIntent = async () => {
    if (!user || !intentPlanId || !PLANS[intentPlanId]) return;
    setUpgradeLoading(true);
    setActionMsg('');
    try {
      const res = await createCheckoutSession({
        planId: intentPlanId,
        userId: user.id,
        email: user.email || profile?.email,
        name: profile?.full_name || profile?.name || undefined,
      });
      if (res?.url) {
        window.location.href = res.url;
      } else {
        throw new Error('URL de checkout não retornada.');
      }
    } catch (e) {
      setActionMsg(`Falha ao iniciar checkout: ${e.message || e}`);
      setUpgradeLoading(false);
    }
  };

  const sub = subscription;
  const status = sub ? statusLabel[sub.status] : null;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-20" data-testid="billing-page">
      <div className="mb-7">
        <h1 className="heading-display text-2xl sm:text-3xl font-bold text-white">Meu Plano</h1>
        <p className="text-sm text-slate-400 mt-1">Gerencie sua assinatura, pagamentos e faturas.</p>
      </div>

      {/* Active plan card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-3xl p-6 sm:p-7 mb-6"
        data-testid="billing-current-plan"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Plano atual</div>
            <div className="mt-1.5 flex items-center gap-2">
              <h2 className="heading-display text-xl sm:text-2xl font-bold text-white">{plan.name}</h2>
              {plan.badge && <PlanBadge type={plan.tier === 'elite' ? 'elite' : 'pro'} size="md" />}
            </div>
            <div className="mt-3 text-2xl sm:text-3xl heading-display font-bold gradient-text">
              {plan.price_label}
              <span className="text-sm text-slate-400 font-normal ml-2">/{plan.period_label}</span>
            </div>
          </div>
          <div className="text-right">
            {status && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.06] text-xs font-semibold text-slate-200" data-testid="billing-status">
                <span className={`w-1.5 h-1.5 rounded-full bg-${status.color}-400`} /> {status.label}
              </div>
            )}
            {sub?.current_period_end && (
              <div className="text-[11px] text-slate-400 mt-2 flex items-center justify-end gap-1.5">
                <Calendar size={11} />
                {sub.cancel_at_period_end ? 'Termina em ' : 'Renova em '}
                {new Date(sub.current_period_end).toLocaleDateString('pt-BR')}
              </div>
            )}
          </div>
        </div>

        {actionMsg && (
          <div className="mt-5 rounded-xl border border-[#4facfe]/30 bg-[#4facfe]/[0.08] px-4 py-3 text-xs text-slate-200 flex items-start gap-2" data-testid="billing-action-msg">
            <Sparkles size={14} className="text-[#4facfe] flex-shrink-0 mt-0.5" />
            {actionMsg}
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/app/plans" className="btn-primary rounded-xl px-5 py-2.5 text-xs font-bold inline-flex items-center gap-2" data-testid="billing-change-plan">
            {plan.tier === 'free' ? 'Fazer upgrade' : 'Trocar plano'}
            <ArrowRight size={12} />
          </Link>
          {sub && plan.tier !== 'free' && (
            <button onClick={openCustomerPortal} disabled={portalLoading} className="glass-card rounded-xl px-5 py-2.5 text-xs font-semibold text-slate-200 hover:text-white inline-flex items-center gap-2 disabled:opacity-60" data-testid="billing-open-portal">
              {portalLoading ? <Loader2 size={12} className="animate-spin" /> : <Settings size={12} />}
              Gerenciar no Stripe
            </button>
          )}
          {sub && plan.tier !== 'free' && !sub.cancel_at_period_end && (
            <button onClick={handleCancel} disabled={portalLoading} className="glass-card rounded-xl px-5 py-2.5 text-xs font-semibold text-slate-300 hover:text-white inline-flex items-center gap-2 disabled:opacity-60" data-testid="billing-cancel">
              <XCircle size={12} /> Cancelar assinatura
            </button>
          )}
        </div>

        {intent === 'upgrade' && intentPlanId && PLANS[intentPlanId] && plan.id !== intentPlanId && (
          <div className="mt-5 rounded-xl border border-[#ffa726]/30 bg-[#ffa726]/[0.08] p-4">
            <div className="text-xs text-slate-300 mb-2">
              Você quer ativar o plano <span className="font-bold text-white">{PLANS[intentPlanId].name}</span> ({PLANS[intentPlanId].price_label}/{PLANS[intentPlanId].period_label}).
            </div>
            <button onClick={handleUpgradeIntent} disabled={upgradeLoading} data-testid="billing-intent-confirm" className="btn-gold rounded-lg px-4 py-2 text-xs font-bold inline-flex items-center gap-2 disabled:opacity-60">
              {upgradeLoading ? (
                <>
                  <Loader2 size={12} className="animate-spin" /> Abrindo checkout...
                </>
              ) : (
                <>
                  Confirmar upgrade <ArrowRight size={12} />
                </>
              )}
            </button>
          </div>
        )}
      </motion.div>

      {/* Payments history */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-card rounded-3xl p-6 sm:p-7 mb-6" data-testid="billing-payments">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard size={16} className="text-[#4facfe]" />
          <h3 className="text-sm font-bold text-white uppercase tracking-widest">Histórico de pagamentos</h3>
        </div>
        {loading ? (
          <div className="text-xs text-slate-400 py-4 text-center">Carregando...</div>
        ) : payments.length === 0 ? (
          <div className="text-xs text-slate-400 py-6 text-center">
            Sem pagamentos registrados.<br/>Você está no plano gratuito.
          </div>
        ) : (
          <ul className="divide-y divide-white/[0.06]">
            {payments.map((p) => (
              <li key={p.id} className="py-3 flex items-center justify-between text-xs">
                <div>
                  <div className="text-white font-medium">{p.description || 'Assinatura GetSpark'}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {new Date(p.created_at).toLocaleDateString('pt-BR')} • {p.method?.toUpperCase() || '-'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-white font-bold">{formatBRL(p.amount_cents)}</div>
                  <div className={`text-[10px] font-semibold ${p.status === 'succeeded' ? 'text-green-400' : p.status === 'failed' ? 'text-red-400' : 'text-slate-400'}`}>
                    {p.status === 'succeeded' ? 'Pago' : p.status === 'failed' ? 'Falhou' : p.status}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </motion.div>

      {/* Invoices */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="glass-card rounded-3xl p-6 sm:p-7" data-testid="billing-invoices">
        <div className="flex items-center gap-2 mb-4">
          <Receipt size={16} className="text-[#ffa726]" />
          <h3 className="text-sm font-bold text-white uppercase tracking-widest">Faturas</h3>
        </div>
        {loading ? (
          <div className="text-xs text-slate-400 py-4 text-center">Carregando...</div>
        ) : invoices.length === 0 ? (
          <div className="text-xs text-slate-400 py-6 text-center">Sem faturas. Suas faturas aparecerão aqui após o primeiro pagamento.</div>
        ) : (
          <ul className="divide-y divide-white/[0.06]">
            {invoices.map((i) => (
              <li key={i.id} className="py-3 flex items-center justify-between text-xs">
                <div>
                  <div className="text-white font-medium">{i.invoice_number || `Fatura #${i.id.slice(0,8)}`}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{new Date(i.created_at).toLocaleDateString('pt-BR')}</div>
                </div>
                <div className="text-right">
                  <div className="text-white font-bold">{formatBRL(i.amount_cents)}</div>
                  <div className={`text-[10px] font-semibold ${i.status === 'paid' ? 'text-green-400' : 'text-slate-400'}`}>{i.status}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </motion.div>
    </div>
  );
};

export default Billing;
